import { Flex, Text } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AGGridDefault, ModalTable, Pagination } from "../../../../components";
import { ColDef } from "ag-grid-community";
import {
  useAnalystPositionDetail,
  useCustomerPositionDetail,
} from "../../../../hooks/usePositions";
import {
  IQueryPosition,
  IQueryPositionDetail,
} from "../../../../interfaces/IQuery";
import { useAuth } from "../../../../context/auth";
import { ERole } from "../../../../interfaces/User/newUser";
import { useTranslation } from "react-i18next";
import { TagCapsules } from "../../../../components";
import IconInfo from "../../components/IconInfo";
import { IColor } from "../../../../utils/selectColors";
import { EOrderSide } from "../../../../interfaces/IQuery";
import dataProcessing from "../../../../utils/dataProcessing";
import { convertToDate } from "../../../../utils/date";
import { EColorIconInfo } from "../../../../interfaces/OrderPostions";
import ModalReject from "../../components/ModalReject";
import { IOrderPositions } from "../../../../interfaces/OrderPostions";

interface IProps {
  query: IQueryPosition;
  id: string;
  setClose: () => void;
}

export default function ModalDetails({ query, id, setClose }: IProps) {
  const { t } = useTranslation();
  const [openModalError, setOpenModalError] = useState({} as IOrderPositions);
  const handleCloseModalError = () => {
    setOpenModalError({} as IOrderPositions);
  };
  const navigate = useNavigate();
  const { roleSelected } = useAuth();
  const isAnalytic = roleSelected === ERole.analyst;
  const [queryDetail, setQueryDetail] = useState<IQueryPositionDetail>({
    id: id,
    page: 1,
    pageSize: 300,
    isAnalytic: isAnalytic,
    client_id: query.client_id || null,
  });
  useEffect(() => {
    setQueryDetail((prev) => ({ ...prev, roleSelected: roleSelected }));
  }, [roleSelected]);

  useEffect(() => {
    if (!!query.client_id) {
      setQueryDetail((prev) => ({ ...prev, client_id: query.client_id }));
    } else {
      setQueryDetail((prev) => ({ ...prev, client_id: null }));
    }
  }, [query]);

  const {
    data: dataAnalystPositionDetail,
    isLoading: isLoadingAnalyst,
    isFetching: isFetchingAnalyst,
  } = useAnalystPositionDetail(queryDetail);
  const {
    data: dataCustumerPositionDetail,
    isLoading: isLoadingCustumer,
    isFetching: isFetchingCustumer,
  } = useCustomerPositionDetail(queryDetail);

  const dataPositionDetail = isAnalytic
    ? dataAnalystPositionDetail?.data
    : dataCustumerPositionDetail;

  const isLoading = isAnalytic ? isLoadingAnalyst : isLoadingCustumer;
  const isFetching = isAnalytic ? isFetchingAnalyst : isFetchingCustumer;
  const pagination = {
    page: queryDetail.page,
    pageSize: queryDetail.pageSize,
    totalElements: dataAnalystPositionDetail?.totalElements,
    totalPages: dataAnalystPositionDetail?.totalPages,
    view: dataAnalystPositionDetail?.data?.length || 0,
  };

  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        hide: !isAnalytic,
        headerName: t("operacoes.posicoes.table.table_head.user_display_name"),
        field: "user_name",
        suppressColumnsToolPanel: true,
        cellRenderer: (params: any) => dataProcessing(params.data?.user_name),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.product_title"),
        field: "product_title",
        suppressColumnsToolPanel: true,
        width: 200,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.product_title),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.is_open"),
        field: "is_open",
        suppressColumnsToolPanel: true,
        width: 110,
        cellRenderer: (params: any) => {
          const isOpen: boolean = !!params.data?.is_open;
          const textStatus = isOpen
            ? t("global.status_open.open")
            : t("global.status_open.close");
          return textStatus ? (
            <TagCapsules border colorTag={isOpen ? IColor.green : IColor.red}>
              {textStatus}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.status_copy"),
        field: "no_filter_status_copy",
        suppressColumnsToolPanel: true,
        width: 200,
        sortable: false,
        cellRenderer: (params: any) => {
          const status: EColorIconInfo | undefined = params.data?.status_copy;
          const textStatus: string | undefined = params.data?.status_copy_text;
          return (
            <Flex gap={4} align="center">
              <IconInfo
                statusCopy={status || EColorIconInfo.WHITE}
                statusCopyText={
                  textStatus || t("operacoes.posicoes.not_information")
                }
              />
              <Text fz="12px" fw="600" mt="4px">
                {textStatus}
              </Text>
            </Flex>
          );
        },
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.seq_num"),
        field: "seq_num",
        suppressColumnsToolPanel: true,
        width: 110,
        cellRenderer: (params: any) => dataProcessing(params.data?.seq_num),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.symbol"),
        field: "symbol",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => dataProcessing(params.data?.symbol),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.side_opening"),
        field: "side_opening",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) => {
          const side: EOrderSide | undefined = params.data?.side_opening;
          const text: string | undefined = params.data?.side_opening_text;
          return text ? (
            <TagCapsules
              border
              colorTag={side === EOrderSide.SELL ? IColor.green : IColor.orange}
            >
              {text}
            </TagCapsules>
          ) : (
            "---"
          );
        },
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.available_quantity"),
        field: "available_quantity",
        suppressColumnsToolPanel: true,
        width: 160,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.available_quantity),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.average_price"),
        field: "average_price",
        suppressColumnsToolPanel: true,
        width: 150,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.average_price),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.buy_average_price"),
        field: "buy_average_price",
        suppressColumnsToolPanel: true,
        width: 170,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.buy_average_price),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.sell_average_price"),
        field: "sell_average_price",
        suppressColumnsToolPanel: true,
        width: 180,
        cellRenderer: (params: any) =>
          dataProcessing(params.data?.sell_average_price),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.profit_loss"),
        field: "profit_loss",
        suppressColumnsToolPanel: true,
        width: 140,
        cellRenderer: (params: any) => dataProcessing(params.data?.profit_loss),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.open_date"),
        field: "open_date",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => convertToDate(params.data?.open_date),
      },
      {
        headerName: t("operacoes.posicoes.table.table_head.close_date"),
        field: "close_date",
        suppressColumnsToolPanel: true,
        width: 120,
        cellRenderer: (params: any) => convertToDate(params.data?.close_date),
      },
    ],
    [t]
  );

  const handleRowClick = (row: any) => {
    if (isAnalytic) {
      const idDetail = row?.id;
      navigate(
        `/operacoes/ordens?position_id=${idDetail}&open_date_start=${query.open_date_start}&open_date_end=${query.open_date_end}` +
          (!!query.client_id ? `&client_id=${query.client_id}` : "")
      );
    }
  };

  return (
    <>
      <ModalTable
        open={!!id}
        setClose={setClose}
        title={t("operacoes.details") || "Detalhes"}
        full={true}
      >
        <AGGridDefault
          title={
            isAnalytic
              ? t("operacoes.posicoes.table.position_opens_client")
              : t("operacoes.posicoes.table.position_open_analyst")
          }
          rowData={dataPositionDetail || []}
          columnDefs={columnDefs}
          isLoading={isLoading || isFetching}
          defaultColDef={
            isAnalytic
              ? {
                  cellStyle: {
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    lineHeight: "23px",
                    cursor: "pointer",
                  },
                }
              : undefined
          }
          pagination={
            isAnalytic ? (
              <Pagination
                pagination={pagination}
                onChange={(page) => {
                  setQueryDetail((prev) => ({ ...prev, page: page }));
                }}
              />
            ) : undefined
          }
          height={isAnalytic ? "450px" : "106px"}
          onRowClick={handleRowClick}
          storageKey={`columns-positions-modal-${
            isAnalytic ? "analyst" : "customer"
          }`}
          context={{
            query: queryDetail,
            setQuery: setQueryDetail,
            hasFilters: isAnalytic,
          }}
        />
      </ModalTable>
      <ModalReject
        handleCloseModalError={handleCloseModalError}
        clorderid={openModalError.clorderid}
        help_reject_url={openModalError.help_reject_url}
        reject_desc={openModalError.reject_desc}
      />
    </>
  );
}
